import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, useTheme } from '@mui/material'

import { IImpact } from 'src/types'
import {
  FairtradeImpactCard,
  FairtradeImpactTitle,
  FairtradeDescriptionWrapper,
  FairtradeImpactAvatar,
  FairtradeDetailsRow,
  FairtradeQuantityBox,
  FairtradeQuantityValue,
  FairtradeQuantityLabel,
  FairtradeMediaWrapper,
  FairtradeBottomSection,
  FairtradeMetaActions,
  ImpactsWrapper,
} from './impacts.styled'
import { RichDescription } from 'src/components/rich-description'
import { ImageCarousel } from 'src/components/image-carousel'
import { LikesCounter } from 'src/components/impacts-cards-ver/likes-counter.component'
import { BlockchainVerifiedBanner } from 'src/components/blockchain-verified-banner'
import { UnSdgList } from 'src/components/impacts-cards-ver/un-sdg-list.component'
import { MediaList } from 'src/components/media-list'
import { WorldHeartIcon } from 'src/assets/icons/v2/WorldHeartIcon'
import { AIGeneratedBanner } from 'src/components/ai-generated-banner'
import { FairtradeFonts } from '../fairtrade-fonts'
import { PageTitle, PageWrapper } from '../fairtrade.styled'

interface IProps {
  items: IImpact[]
  showUnSdg: boolean
  primaryColor: string | null
  walletAddress: string
  showWalletAddress: boolean
  showBcInfo: boolean
  accountImage: string | null
}

export const FairtradeImpactsTemplate = ({
  items,
  showUnSdg,
  primaryColor,
  walletAddress,
  showWalletAddress,
  showBcInfo,
  accountImage,
}: IProps) => {
  const [t] = useTranslation()
  // const [isMobile] = useState<boolean>(window.innerWidth < 770)
  const theme = useTheme()

  const impactsToShow = useMemo(() => items ?? [], [items])

  if (!impactsToShow.length) {
    return null
  }

  return (
    <PageWrapper
      sx={{
        gap: '95px',
      }}
    >
      <FairtradeFonts />

      <PageTitle
        sx={{
          marginX: {
            xs: '24px',
            md: '8px',
          },
        }}
      >
        {t('impacts-header-title')}
      </PageTitle>

      <ImpactsWrapper>
        {impactsToShow.map((impact, index) => {
          const impactIcon = impact.image_medium || impact.image
          const hasImpactIcon = !!impactIcon && !impactIcon.includes('ui-avatars.com/api')
          const mainMedia = impact.mainMedia?.length ? impact.mainMedia : []
          const attachments =
            impact.media?.filter(
              (mediaItem) => !mainMedia.some((main) => main.id === mediaItem.id),
            ) ?? []
          const attachmentsCount = attachments.length

          return (
            <FairtradeImpactCard key={impact.id} id={impact.id} isOdd={index % 2 === 0}>
              <FairtradeImpactTitle>{impact.name}</FairtradeImpactTitle>
              <FairtradeDescriptionWrapper>
                {hasImpactIcon ? (
                  <FairtradeImpactAvatar src={impactIcon} alt={impact.name} />
                ) : (
                  <Box
                    sx={{
                      width: 152,
                      height: 152,
                      float: 'right',
                      marginLeft: theme.spacing(3),
                      marginBottom: theme.spacing(2),
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(23, 34, 49, 0.08)',
                      color: theme.palette.text.secondary,
                      shapeOutside: 'circle(50%)',
                      [theme.breakpoints.down('md')]: {
                        float: 'none',
                        marginLeft: 0,
                        marginRight: 'auto',
                        marginBottom: theme.spacing(2),
                        shapeOutside: 'none',
                      },
                    }}
                  >
                    <WorldHeartIcon size={72} color={theme.palette.text.secondary} />
                  </Box>
                )}
                {impact.description && impact.description !== '<p><br></p>' && (
                  <RichDescription
                    html={impact.description}
                    sx={{
                      '& p': {
                        lineHeight: 1.6,
                        fontSize: '1rem',
                        marginBottom: theme.spacing(2),
                      },
                    }}
                  />
                )}
              </FairtradeDescriptionWrapper>

              {mainMedia.length > 0 && (
                <FairtradeMediaWrapper>
                  <ImageCarousel images={mainMedia} isStep />
                </FairtradeMediaWrapper>
              )}

              <FairtradeDetailsRow>
                <FairtradeQuantityBox>
                  <FairtradeQuantityValue>{impact.quantity}</FairtradeQuantityValue>
                  <FairtradeQuantityLabel>{impact.quantity_type_name}</FairtradeQuantityLabel>
                </FairtradeQuantityBox>

                <FairtradeMetaActions>
                  <LikesCounter
                    count={impact.totalLikes}
                    userLiked={impact.liked}
                    impactId={impact.id}
                  />
                </FairtradeMetaActions>
              </FairtradeDetailsRow>

              <FairtradeBottomSection>
                {showUnSdg && !!impact.un_sdg?.length && <UnSdgList unSdg={impact.un_sdg} />}

                {attachmentsCount > 0 && <MediaList media={attachments} />}
              </FairtradeBottomSection>

              {showBcInfo && impact.scans && impact.scans.length > 0 && (
                <BlockchainVerifiedBanner
                  size={44}
                  scans={impact.scans}
                  stroke={primaryColor || undefined}
                  walletAddress={walletAddress}
                  showWalletAddress={showWalletAddress}
                  impactImage={impact.image}
                  impactName={impact.name}
                  accountImage={accountImage}
                  sx={{
                    top: 'auto',
                    left: 'auto',
                    right: 6,
                    bottom: 8,
                  }}
                />
              )}

              {impact.ai_generated && (
                <Box sx={{ mb: 2 }}>
                  <AIGeneratedBanner
                    sx={{
                      top: 'auto',
                      bottom: 0,
                    }}
                  />
                </Box>
              )}
            </FairtradeImpactCard>
          )
        })}
      </ImpactsWrapper>
    </PageWrapper>
  )
}
